import { Component, forwardRef, inject, Input, OnInit } from '@angular/core';
import { AbstractControl, FormsModule, NG_VALIDATORS, ValidationErrors, Validator } from '@angular/forms';
import { PhonePrefixSanitizerDirective } from '@lx/quote-tool/shared/ui/directives';
import { ResidenceCountry, ResidenceCountryService } from '@lx/shared/data-access/destination';
import { DropdownModule } from 'primeng/dropdown';
import { InputMaskModule } from 'primeng/inputmask';
import { InputNumberModule } from 'primeng/inputnumber';
import { first } from 'rxjs';
import { SelectorField } from '@lx/quote-tool/lite/shared/ui/directives';
import { getCustomFormComponentControlValueAccessorProvider, minPhoneNumberLength } from '@lx/quote-tool/lite/shared/utils';
import { DropdownSidePanelFieldComponent } from '../dropdown-side-panel-field/dropdown-side-panel-field.component';
import { MINIMUM_PHONE_LENGTH } from '@lx/quote-tool/shared/validators';
import { KeyboardFocusDirective } from '@lx/shared/ui/keyboard-focus';

@Component({
  standalone: true,
  selector: 'lx-phone-number-field',
  templateUrl: './phone-number-field.component.html',
  styleUrls: ['./phone-number-field.component.scss'],
  imports: [
    DropdownModule,
    FormsModule,
    InputNumberModule,
    DropdownSidePanelFieldComponent,
    PhonePrefixSanitizerDirective,
    InputMaskModule,
    KeyboardFocusDirective,
  ],
  providers: [
    getCustomFormComponentControlValueAccessorProvider(PhoneNumberFieldComponent),
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneNumberFieldComponent),
      multi: true,
    },
  ],
})
export class PhoneNumberFieldComponent extends SelectorField<string> implements OnInit, Validator {
  @Input() title: string;

  phoneCode = '';
  phoneNumber = '';

  residenceCountries: ResidenceCountry[] | undefined;
  prefixes: string[] = [];

  private readonly residenceCountryService = inject(ResidenceCountryService);

  ngOnInit() {
    this.loadData();
  }

  updatePhoneCode(phoneCode: string) {
    this.phoneCode = phoneCode;
    this.updatePhone();
  }

  updatePhoneNumber(phoneNumber: string) {
    this.phoneNumber = phoneNumber;
    this.updatePhone();
  }

  updatePhone() {
    const phone = this.phoneNumber ? `${this.phoneCode} ${this.phoneNumber}` : this.phoneCode || '';
    this.onModelChange(phone);
  }

  writeValue(phone: string): void {
    if (phone) {
      [this.phoneCode, this.phoneNumber] = phone.split(' ');
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return minPhoneNumberLength(MINIMUM_PHONE_LENGTH)(control);
  }

  private loadData(): void {
    this.residenceCountryService
      .getAll()
      .pipe(first())
      .subscribe((residenceCountries: ResidenceCountry[]) => {
        this.residenceCountries = residenceCountries;
        this.prefixes = residenceCountries?.map((c) => c.phoneCode).filter(Boolean) ?? [];
        this.updatePhoneNumber(this.phoneNumber || '');
      });
  }
}
